import { filter } from 'lodash';
import { Machine, assign } from 'xstate';

type Step = {
  // Unique string used to identify the step.
  key: string;
  // The title displayed when the step is active.
  title: string;
  // A function that returns `true` if the step is valid for the user's flow in the contact form.
  cond: (context: Context) => boolean;
  // True if the step is represented by a numbered dot.
  hasDot: boolean;
  // A function that returns `true` if the feedback form should display beneath the step content.
  hasFeedback: (context: Context) => boolean;
};

type CurrentStep = Pick<Step, 'key' | 'title'> & {
  hasFeedback: boolean;
};

type Context = {
  // ===== These context props must be set in the useMachine hook. =====
  // True if the user's question has any related helpdoc articles.
  hasDocs: boolean;
  // True if the user's question has any related community conversations.
  hasCommunity: boolean;
  // True if the user's question allows them to contact us.
  hasContact: boolean;
  // True if the user is eligible to contact support
  isEmailSupportEligible: boolean;
  // A callback to track the path of steps the user has taken.
  trackPath: (path: string) => void;
  // Called to unconfirm the user's question selection.
  unconfirm: () => void;
  // Skip the interstitial step
  skipInterstitial?: boolean;

  // ====== These context props will be set by the machine itself. =====
  // An array of the steps that are valid for the user's flow.
  steps: Step[];
  // An array of the steps that are represented by numbered dots.
  dotSteps: Step[];
  // The index of the current step.
  stepIndex: number;
  // The index of the currently highlighted dot.
  dotIndex: number;
  // The Step object of the current step.
  currentStep: CurrentStep | null;
  // An array of tooltips displayed over the number dots.
  dotTooltips: string[];
  // A map of Step keys to their index in the array. Used to navigate to an arbitrary step.
  keyIndexMap: { [key: string]: number };
  // A map of Step keys to their index in the dot steps array. Used to highlight a step's dot.
  dotKeyIndexMap: { [key: string]: number };
};

type StateSchema = {
  states: {
    initial: {};
    step: {};
    unconfirm: {};
  };
};

type NextEvent = { type: 'NEXT_STEP' };
type PrevEvent = { type: 'PREV_STEP' };
type DotEvent = { type: 'STEP_FROM_DOT'; dotIndex: number };
type ContactEvent = { type: 'CONTACT' };

type Events = NextEvent | PrevEvent | DotEvent | ContactEvent;

const STEPS: Step[] = [
  {
    key: 'interstitial',
    title: "We're finding the right help for you",
    cond: ({ hasDocs, hasCommunity, hasContact, skipInterstitial }) =>
      !skipInterstitial && (hasDocs || hasCommunity) && hasContact,
    hasDot: false,
    hasFeedback: () => false,
  },
  {
    key: 'docs',
    title: 'Review our help articles',
    cond: ({ hasDocs }) => hasDocs,
    hasDot: true,
    hasFeedback: ({ steps, stepIndex }) => stepIndex < steps.length - 1,
  },
  {
    key: 'community',
    title: 'Check Community conversations',
    cond: ({ hasCommunity }) => hasCommunity,
    hasDot: true,
    hasFeedback: ({ steps, stepIndex }) => stepIndex < steps.length - 1,
  },
  {
    key: 'contact',
    title: 'Email Support',
    cond: ({ hasContact }) => hasContact,
    hasDot: true,
    hasFeedback: () => false,
  },
  {
    key: 'upgrade',
    title: 'Upgrade to email Support',
    cond: ({ isEmailSupportEligible }) => !isEmailSupportEligible,
    hasDot: false,
    hasFeedback: () => false,
  },
];

// This machine traverses the ordered steps of the help section of the contact form.
const helpMachine = Machine<Context, StateSchema, Events>(
  {
    id: 'help',
    initial: 'initial',
    context: {
      hasDocs: false,
      hasCommunity: false,
      hasContact: false,
      isEmailSupportEligible: false,
      trackPath: () => {},
      unconfirm: () => {},
      steps: [],
      dotSteps: [],
      stepIndex: 0,
      dotIndex: 0,
      currentStep: null,
      dotTooltips: [],
      keyIndexMap: {},
      dotKeyIndexMap: {},
    },
    states: {
      // This trasient step is used to setup many of the necessary context objects.
      // Then it immediately proceeds to the first step.
      initial: {
        entry: ['initialize'],
        on: {
          '': [
            {
              target: 'step',
            },
          ],
        },
      },

      // This state represents the user being at any step in the help flow.
      step: {
        entry: ['trackPath', 'setCurrentStep', 'setDotIndex'],
        on: {
          // Called when the user clicks a button to go to the next step.
          NEXT_STEP: [
            {
              cond: 'hasNextStep',
              actions: 'incrementStep',
              target: 'step',
            },
          ],

          // Called when the user clicks a button to return to the previous step.
          PREV_STEP: [
            {
              cond: 'hasPrevStep',
              actions: 'decrementStep',
              target: 'step',
            },
            {
              target: 'unconfirm',
            },
          ],

          // Called when a user clicks a dot to go to an arbitrary step.
          STEP_FROM_DOT: [
            {
              actions: 'setStepFromDotIndex',
              target: 'step',
            },
          ],

          // Called when the user skips directly to the contact form.
          CONTACT: [
            {
              actions: 'setStepToContact',
              target: 'step',
            },
          ],
        },
      },

      // In this state, the user has unconfirmed their previous selections and exited the help flow.
      unconfirm: {
        entry: ['unconfirm'],
        type: 'final',
      },
    },
  },
  {
    actions: {
      // Unconfirm the user's choices in the parent form machine.
      unconfirm: ({ unconfirm }) => unconfirm(),

      // Construct all the objects required by the machine to step through the help flow.
      // The resulting objects are described in the Context type definition.
      initialize: assign((context: Context) => {
        const steps = filter(STEPS, ({ cond }) => cond(context));
        const dotSteps = filter(steps, ({ hasDot }) => hasDot);
        const dotTooltips = dotSteps.map(({ title }) => title);

        const mapKeys = (object: {}) => ({ key }: Step, index: number) => {
          object[key] = index;
        };

        const keyIndexMap = {};
        steps.forEach(mapKeys(keyIndexMap));

        const dotKeyIndexMap = {};
        dotSteps.forEach(mapKeys(dotKeyIndexMap));

        return {
          steps,
          dotSteps,
          dotTooltips,
          keyIndexMap,
          dotKeyIndexMap,
        };
      }),

      // Proceed to the next step in the help flow.
      incrementStep: assign({ stepIndex: ({ stepIndex }) => stepIndex + 1 }),

      // Return to the previous step in the help flow.
      decrementStep: assign({ stepIndex: ({ stepIndex }) => stepIndex - 1 }),

      // When the user clicks a dot, navigate to the correct step by setting the `stepIndex`.
      // 1. Get the index of the clicked dot.
      // 2. Use the `dotSteps` array to get the Step object of that dot.
      // 3. Get the `key` of that step.
      // 4. Use the `key` to look up the step's index in the `keyIndexMap`.
      setStepFromDotIndex: assign({
        stepIndex: ({ keyIndexMap, dotSteps }, { dotIndex }: DotEvent) => {
          const { key } = dotSteps[dotIndex];
          return keyIndexMap[key];
        },
      }),

      // Get the index of the `contact` step and set that to the `stepIndex`.
      setStepToContact: assign({
        stepIndex: ({ keyIndexMap }) => keyIndexMap.contact,
      }),

      // When traversing to a step, highlight the corresponding dot by setting the `dotIndex`.
      // If a step has no corresponding dot then do not change the current `dotIndex`.
      // This allows both the `contact` an `upgrade` steps to show the final dot highlighted.
      setDotIndex: assign({
        dotIndex: ({ dotIndex, dotKeyIndexMap, currentStep: { key } }) => {
          const index = dotKeyIndexMap[key];
          return index >= 0 ? index : dotIndex;
        },
      }),

      // Set the `currentStep` from the current `stepIndex` so
      // the step data is available to the Help component.
      setCurrentStep: assign({
        currentStep: context => {
          const { steps, stepIndex } = context;
          const { hasFeedback, key, title } = steps[stepIndex];

          return {
            key,
            title,
            hasFeedback: hasFeedback(context),
          };
        },
      }),

      // Create a string from the keys of the steps that the user has traversed
      // and send that to the `trackPath` callback.
      trackPath: ({ stepIndex, steps, trackPath }) => {
        const path = steps
          .slice(0, stepIndex + 1)
          .map(({ key }) => key)
          .join('.');
        trackPath(path);
      },
    },
    guards: {
      hasPrevStep: ({ stepIndex }) => stepIndex > 0,
      hasNextStep: ({ stepIndex, steps }) => stepIndex < steps.length - 1,
    },
  }
);

export default helpMachine;
